// src/utils/countryFlags.js
// Centralized country → flag logic
// Used by PlayerProfile, AgentProfile, ScoutProfile, etc.

// Map normalized country/nationality names → ISO flag codes
// NOTE: UK football nations intentionally map to GB for flag display
export const COUNTRY_TO_ISO = {
    // Europe
    albania: 'al',
    andorra: 'ad',
    austria: 'at',
    belarus: 'by', // ✅ THIS WAS MISSING
    belgium: 'be',
    bosnia_and_herzegovina: 'ba',
    bulgaria: 'bg',
    croatia: 'hr',
    cyprus: 'cy',
    czech_republic: 'cz',
    denmark: 'dk',
    estonia: 'ee',
    finland: 'fi',
    france: 'fr',
    germany: 'de',
    greece: 'gr',
    hungary: 'hu',
    iceland: 'is',
    ireland: 'ie',
    italy: 'it',
    latvia: 'lv',
    liechtenstein: 'li',
    lithuania: 'lt',
    luxembourg: 'lu',
    malta: 'mt',
    moldova: 'md',
    monaco: 'mc',
    montenegro: 'me',
    netherlands: 'nl',
    north_macedonia: 'mk',
    norway: 'no',
    poland: 'pl',
    portugal: 'pt',
    romania: 'ro',
    russia: 'ru',
    san_marino: 'sm',
    serbia: 'rs',
    slovakia: 'sk',
    slovenia: 'si',
    spain: 'es',
    sweden: 'se',
    switzerland: 'ch',
    ukraine: 'ua',
    vatican_city: 'va',

    // UK football nations (intentional)
    england: 'gb',
    scotland: 'gb',
    wales: 'gb',
    northern_ireland: 'gb',

    // Africa
    algeria: 'dz',
    angola: 'ao',
    benin: 'bj',
    botswana: 'bw',
    burkina_faso: 'bf',
    burundi: 'bi',
    cameroon: 'cm',
    cabo_verde: 'cv',
    central_african_republic: 'cf',
    chad: 'td',
    comoros: 'km',
    congo: 'cg',
    'congo_congo-brazzaville': 'cg',
    democratic_republic_of_the_congo: 'cd',
    djibouti: 'dj',
    egypt: 'eg',
    equatorial_guinea: 'gq',
    eritrea: 'er',
    eswatini: 'sz',
    ethiopia: 'et',
    gabon: 'ga',
    gambia: 'gm',
    ghana: 'gh',
    guinea: 'gn',
    guinea_bissau: 'gw',
    ivory_coast: 'ci',
    kenya: 'ke',
    lesotho: 'ls',
    liberia: 'lr',
    libya: 'ly',
    madagascar: 'mg',
    malawi: 'mw',
    mali: 'ml',
    mauritania: 'mr',
    mauritius: 'mu',
    morocco: 'ma',
    mozambique: 'mz',
    namibia: 'na',
    niger: 'ne',
    nigeria: 'ng',
    rwanda: 'rw',
    sao_tome_and_principe: 'st',
    senegal: 'sn',
    seychelles: 'sc',
    sierra_leone: 'sl',
    somalia: 'so',
    south_africa: 'za',
    south_sudan: 'ss',
    sudan: 'sd',
    tanzania: 'tz',
    togo: 'tg',
    tunisia: 'tn',
    uganda: 'ug',
    zambia: 'zm',
    zimbabwe: 'zw',

    // Americas
    antigua_and_barbuda: 'ag',
    argentina: 'ar',
    bahamas: 'bs',
    barbados: 'bb',
    belize: 'bz',
    bolivia: 'bo',
    brazil: 'br',
    canada: 'ca',
    chile: 'cl',
    colombia: 'co',
    costa_rica: 'cr',
    cuba: 'cu',
    dominica: 'dm',
    dominican_republic: 'do',
    ecuador: 'ec',
    el_salvador: 'sv',
    grenada: 'gd',
    guatemala: 'gt',
    guyana: 'gy',
    haiti: 'ht',
    honduras: 'hn',
    jamaica: 'jm',
    mexico: 'mx',
    nicaragua: 'ni',
    panama: 'pa',
    paraguay: 'py',
    peru: 'pe',
    saint_kitts_and_nevis: 'kn',
    saint_lucia: 'lc',
    saint_vincent_and_the_grenadines: 'vc',
    suriname: 'sr',
    trinidad_and_tobago: 'tt',
    united_states: 'us',
    uruguay: 'uy',
    venezuela: 've',

    // Asia
    afghanistan: 'af',
    armenia: 'am',
    azerbaijan: 'az',
    bahrain: 'bh',
    bangladesh: 'bd',
    bhutan: 'bt',
    brunei: 'bn',
    cambodia: 'kh',
    china: 'cn',
    georgia: 'ge',
    india: 'in',
    indonesia: 'id',
    iran: 'ir',
    iraq: 'iq',
    israel: 'il',
    japan: 'jp',
    jordan: 'jo',
    kazakhstan: 'kz',
    kuwait: 'kw',
    kyrgyzstan: 'kg',
    laos: 'la',
    lebanon: 'lb',
    malaysia: 'my',
    maldives: 'mv',
    mongolia: 'mn',
    myanmar_burma: 'mm',
    nepal: 'np',
    north_korea: 'kp',
    oman: 'om',
    pakistan: 'pk',
    palestine: 'ps',
    philippines: 'ph',
    qatar: 'qa',
    saudi_arabia: 'sa',
    singapore: 'sg',
    south_korea: 'kr',
    sri_lanka: 'lk',
    syria: 'sy',
    taiwan: 'tw',
    tajikistan: 'tj',
    thailand: 'th',
    timor_leste: 'tl',
    turkey: 'tr',
    turkmenistan: 'tm',
    united_arab_emirates: 'ae',
    uzbekistan: 'uz',
    vietnam: 'vn',
    yemen: 'ye',

    // Oceania
    australia: 'au',
    fiji: 'fj',
    kiribati: 'ki',
    marshall_islands: 'mh',
    micronesia: 'fm',
    nauru: 'nr',
    new_zealand: 'nz',
    palau: 'pw',
    papua_new_guinea: 'pg',
    samoa: 'ws',
    solomon_islands: 'sb',
    tonga: 'to',
    tuvalu: 'tv',
    vanuatu: 'vu',
};

// Normalize nationality → ISO flag code
export const getFlagCode = (nationality) => {
    if (!nationality) return null;

    const key = nationality
        .toLowerCase()
        .replace(/[()]/g, '')
        .replace(/\s+/g, '_')
        .trim();

    return COUNTRY_TO_ISO[key] || null;
};
